import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AvatarFallback } from './partials/profileHelpers';
import AvatarWithDesignerBadge from './partials/AvatarWithDesignerBadge';

const API = process.env.REACT_APP_API_URL || '';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [bubbleColor, setBubbleColor] = useState(null);
  const [user, setUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem('pulinaUser');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed;
    } catch {
      return null;
    }
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginErrors, setLoginErrors] = useState([]);
  const [recaptchaToken, setRecaptchaToken] = useState(false);
  const usernameInputRef = useRef(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  const openLogin = () => {
    setLoginErrors([]);
    setIsDialogOpen(true);
  };
  const closeLogin = () => setIsDialogOpen(false);

  const login = (event) => {
    event.preventDefault();
    const errors = [];
    const form = event.currentTarget;
    const usernameValue = (form.elements.username?.value ?? '').trim();
    const passwordValue = form.elements.password?.value ?? '';
    
    if (!usernameValue || !passwordValue) {
      errors.push('Käyttäjänimi tai sähköposti ja salasana vaaditaan.');
    }
    if (!recaptchaToken) {
      errors.push('Etkö ole ihminen?');
    }
    
    if (errors.length > 0) {
      setLoginErrors(errors);
      return;
    }
    
    setLoginErrors([]);
    fetch(`${API}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: usernameValue, password: passwordValue }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.user) {
          setUser({ ...data.user });
          setIsDialogOpen(false);
          setPassword('');
          setLoginErrors([]);
          setShowCookieBanner(true);
        } else {
          setLoginErrors([data.message || 'Kirjautuminen epäonnistui.']);
        }
      })
      .catch(() => {
        setLoginErrors(['Yhteysvirhe. Yritä uudelleen.']);
      });
  };

  const updateDisplayName = (newName) => {
    setUser((prev) => {
      if (!prev) return prev;
      const cleaned = newName && newName.trim().length > 0 ? newName.trim() : prev.username;
      return { ...prev, displayName: cleaned };
    });
  };

  const patchUser = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const logout = () => {
    setUser(null);
    setUsername('');
    setPassword('');
    setIsDialogOpen(false);
    // Explicitly clear localStorage
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('pulinaUser');
      }
    } catch {
      // ignore storage errors
    }
  };

  // Sync user state with localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (user) {
        window.localStorage.setItem('pulinaUser', JSON.stringify(user));
      } else {
        window.localStorage.removeItem('pulinaUser');
      }
    } catch {
      // ignore storage errors
    }
  }, [user]);

  // Fetch user settings (e.g. bubbleColor) when user is set
  useEffect(() => {
    if (!user?.id) {
      setBubbleColor(null);
      return;
    }
    fetchAvatarState(user.id)
      .then(({ bubbleColor: hex }) => setBubbleColor(hex))
      .catch(() => setBubbleColor(null));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const onSettingsUpdated = (e) => {
      const detailUserId = e.detail?.userId != null ? String(e.detail.userId) : null;
      if (detailUserId !== String(user.id)) return;
      fetchAvatarState(user.id)
        .then(({ bubbleColor: hex }) => setBubbleColor(hex))
        .catch(() => {});
    };
    window.addEventListener('user-settings-updated', onSettingsUpdated);
    return () => window.removeEventListener('user-settings-updated', onSettingsUpdated);
  }, [user?.id]);

  // Focus username input when dialog opens
  useEffect(() => {
    if (isDialogOpen && usernameInputRef.current) {
      // Small delay to ensure the modal is rendered
      setTimeout(() => {
        usernameInputRef.current?.focus();
      }, 0);
    }
  }, [isDialogOpen]);

  const value = {
    user,
    openLogin,
    logout,
    updateDisplayName,
    patchUser,
    bubbleColor,
    showCookieBanner,
    setShowCookieBanner,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {showCookieBanner && (
        <div className="CookieBanner">
          <p className="CookieBanner-text">
            Käytämme vain välttämättömiä keksejä. Emme koskaan käytä tietojasi mihinkään muuhun, kuin tämän sivun toiminnallisuuksiin.
          </p>
          <button
            type="button"
            className="Button CookieBanner-button"
            onClick={() => {
              setShowCookieBanner(false);
            }}
          >
            OK
          </button>
        </div>
      )}

      {isDialogOpen && (
        <div className="Modal-backdrop">
          <div className="Modal">
            <h2>Kirjaudu sisään</h2>
            <form onSubmit={login}>
              {loginErrors.length > 0 && (
                <div className="loginerrors" role="alert">
                  {loginErrors.map((error, index) => (
                    <div key={index} className="Modal-error">
                      {error}
                    </div>
                  ))}
                </div>
              )}
              <label>
                Käyttäjänimi tai E-mail
                <input
                  ref={usernameInputRef}
                  id="username"
                  name="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                />
              </label>
              <label>
                Salasana
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label htmlFor='recaptcha'>
                Ihminen?
              </label>
              <input type="checkbox" id="recaptcha" name="recaptcha" onClick={() => setRecaptchaToken(!recaptchaToken)} />

              <div className="Modal-actions">
                <button type="submit" className="Button">
                  Kirjaudu
                </button>
                <button
                  type="button"
                  className="Button Button-secondary"
                  onClick={closeLogin}
                >
                  Peruuta
                </button>
              </div>
            </form>
            <a href="/uusi">Uusi käyttäjä? Rekisteröidy.</a><br />
            <a href="/password-reset">Unohditko salasanasi?</a>
            
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

function fetchAvatarState(userId) {
  return Promise.all([
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`).then((res) => res.json()),
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`).then((res) => res.json()),
  ]).then(([profileData, settingsData]) => {
    const showAvatar = profileData && (profileData.showAvatar === 'YES' || profileData.avatar === 'YES');
    const hex = settingsData?.bubbleColor != null && typeof settingsData.bubbleColor === 'string' ? settingsData.bubbleColor.trim() : null;
    const avatarDesigner =
      settingsData && typeof settingsData === 'object' ? settingsData.avatarDesigner ?? null : null;
    return { showAvatar: showAvatar ? 'YES' : 'NO', bubbleColor: hex, avatarDesigner };
  });
}

export function AuthPanel() {
  const { user, openLogin } = useAuth();
  const [avatarVisible, setAvatarVisible] = useState(null); // 'YES' | 'NO' | null
  const [bubbleColor, setBubbleColor] = useState(null);
  const [avatarDesigner, setAvatarDesigner] = useState(null);
  const [avatarImageOk, setAvatarImageOk] = useState(true);
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);

  useEffect(() => {
    if (!user?.id) {
      setAvatarVisible(null);
      setBubbleColor(null);
      setAvatarDesigner(null);
      setAvatarImageOk(true);
      return;
    }
    fetchAvatarState(user.id)
      .then(({ showAvatar, bubbleColor: hex, avatarDesigner: ad }) => {
        setAvatarVisible(showAvatar);
        setBubbleColor(hex);
        setAvatarDesigner(ad);
        setAvatarImageOk(true);
      })
      .catch(() => {
        setAvatarVisible('NO');
        setBubbleColor(null);
        setAvatarDesigner(null);
        setAvatarImageOk(true);
      });
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const onSettingsUpdated = (e) => {
      const detailUserId = e.detail?.userId != null ? String(e.detail.userId) : null;
      if (detailUserId !== String(user.id)) return;
      fetchAvatarState(user.id)
        .then(({ showAvatar, bubbleColor: hex, avatarDesigner: ad }) => {
          setAvatarVisible(showAvatar);
          setBubbleColor(hex);
          setAvatarDesigner(ad);
          setAvatarImageOk(true);
          setAvatarRefreshKey((k) => k + 1);
        })
        .catch(() => {});
    };
    window.addEventListener('user-settings-updated', onSettingsUpdated);
    return () => window.removeEventListener('user-settings-updated', onSettingsUpdated);
  }, [user?.id]);

  if (!user) {
    return (
      <div className="Button" onClick={openLogin}>
        Kirjaudu
      </div>
    );
  }

  const showAvatarImage = avatarVisible === 'YES' && avatarImageOk;
  const showAvatarFallback = avatarVisible === 'YES' && !avatarImageOk;

  return (
    <div className="Auth-panel">
      <div className="Auth-user">
        <a href="/asetukset" className={`Auth-user-link${user.role && user.role !== 'user' ? ' Auth-user-link--member' : ''}`}>
          {avatarVisible === 'NO' || avatarVisible === null ? (
            <span className="Auth-username-only">{user.displayName || user.username}</span>
          ) : showAvatarImage ? (
            <AvatarWithDesignerBadge key={avatarRefreshKey} designer={avatarDesigner} variant="menu">
              <img
                src={`${API}/api/users/${encodeURIComponent(user.id)}/avatar?t=${avatarRefreshKey}`}
                alt=""
                className="Auth-avatar"
                onError={() => setAvatarImageOk(false)}
              />
            </AvatarWithDesignerBadge>
          ) : showAvatarFallback ? (
            <AvatarWithDesignerBadge designer={avatarDesigner} variant="menu-fallback">
              <AvatarFallback
                bubbleColorHex={bubbleColor}
                displayName={user.displayName}
                username={user.username}
                size="header"
              />
            </AvatarWithDesignerBadge>
          ) : null}
        </a>
      </div>
    </div>
  );
}

